import type { ComboboxOptions } from '@library/components/selects';
import type { CreateAdjustment } from '@library/types/members/memberAdjustments';
import { FormActionButtons } from '@library/components/buttons';
import type { QueryMutationError } from '@bo-types/common';
import { SecondaryButton } from '@library/components/buttons/SecondaryButton';
import { useMemo } from 'react';

export interface CreateMemberAdjustmentConfirmationProps {
  adjustment: CreateAdjustment;
  adjustmentReasonOptions: ComboboxOptions;
  createMemberAdjustmentError: QueryMutationError;
  isCreateMemberAdjustmentApiUninitialized: boolean;
  isCreateMemberAdjustmentError: boolean;
  isCreateMemberAdjustmentLoading: boolean;
  isCreateMemberAdjustmentSuccess: boolean;
  onClickBack: () => void;
  onClickClose: () => void;
  onConfirmAdjustment: () => void;
}

export const CreateMemberAdjustmentConfirmation = ({
  adjustment,
  adjustmentReasonOptions,
  createMemberAdjustmentError,
  isCreateMemberAdjustmentApiUninitialized,
  isCreateMemberAdjustmentError,
  isCreateMemberAdjustmentLoading,
  isCreateMemberAdjustmentSuccess,
  onClickBack,
  onClickClose,
  onConfirmAdjustment
}: CreateMemberAdjustmentConfirmationProps) => {
  const { adjustmentAmount, adjustmentReasonId, adjustmentType } = adjustment;

  const selectedAdjustmentReasonName = useMemo(
    () =>
      adjustmentReasonOptions.find(
        (reasonOption) => reasonOption.value === adjustmentReasonId
      )?.label,
    [adjustmentReasonOptions, adjustmentReasonId]
  );

  const hasErrorMessage =
    createMemberAdjustmentError && 'message' in createMemberAdjustmentError;

  const errorMessage = hasErrorMessage
    ? createMemberAdjustmentError.message
    : 'The following adjusment has failed to apply to the member account.';

  const renderActionButtons = () => {
    if (
      isCreateMemberAdjustmentApiUninitialized ||
      isCreateMemberAdjustmentLoading
    ) {
      return (
        <FormActionButtons
          className="pr-6"
          isLoading={isCreateMemberAdjustmentLoading}
          primary={{
            disabled: isCreateMemberAdjustmentLoading,
            label: 'Confirm',
            onClick: onConfirmAdjustment,
            title: 'Confirm'
          }}
          secondary={{ label: 'Back', onClick: onClickBack, title: 'Back' }}
        />
      );
    }

    return (
      <div className="border-soFresh-140 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
        {isCreateMemberAdjustmentError ? (
          <SecondaryButton
            title="Back"
            handleOnClick={onClickBack}
            testId="create-adjustment-confirmation-back"
          >
            {' '}
            Back
          </SecondaryButton>
        ) : (
          <SecondaryButton
            title="Close"
            handleOnClick={onClickClose}
            testId="create-adjustment-confirmation-close"
          >
            Close
          </SecondaryButton>
        )}
      </div>
    );
  };

  const renderMessage = () => {
    if (isCreateMemberAdjustmentError) {
      return (
        <p
          className="text-roses text-wrap pb-2"
          data-testid="create-adjusment-save-error-message"
        >
          {errorMessage}
        </p>
      );
    }

    if (
      !isCreateMemberAdjustmentApiUninitialized &&
      isCreateMemberAdjustmentSuccess
    ) {
      return (
        <p
          className="text-atlien-100 text-wrap pb-2"
          data-testid="create-adjusment-save-success-message"
        >
          The following adjusment was successfully applied to the member
          account.
        </p>
      );
    }

    return (
      <p
        className="text-soClean-180 text-wrap pb-2"
        data-testid="create-adjusment-save-confirm-message"
      >
        Are you sure you want to create the following adjustment for the
        account?
      </p>
    );
  };

  return (
    <div data-testid="create-adjustment-confirmation-">
      <div className="flex flex-col gap-2 p-6 text-sm">
        {renderMessage()}
        <p data-testid="confirm-adjusment-amount">
          <span className="text-soClean-180">Amount: </span>
          <span className="text-soClean-100">{adjustmentAmount}</span>
        </p>
        <p data-testid="confirm-adjusment-reason">
          <span className="text-soClean-180">Reason: </span>
          <span className="text-soClean-100">
            {selectedAdjustmentReasonName}
          </span>
        </p>
        <p data-testid="confirm-adjusment-type">
          <span className="text-soClean-180">Type: </span>
          <span className="text-soClean-100">{adjustmentType}</span>
        </p>
      </div>
      <div className="py-2">{renderActionButtons()}</div>
    </div>
  );
};
